'use client';
import { forwardRef, ReactElement, ReactNode, Ref } from 'react';
import { ComboboxSelect } from './combobox-select';
import { ComboboxInput } from './combobox-input';

interface Item {
    value: string;
    label: string;
}

interface ComboboxProps<T extends Item> {
    items: T[];
    filterItems?: (value: string) => void;
    onChange: (value: string | undefined) => void;
    renderItem: (item: T) => ReactNode;
    renderSelection?: (item: T) => ReactNode;
    onValueInput?: (value: string) => void;
    name: string;
    value: T | undefined;
    loading?: boolean;
    loadingMessage?: string;
    disabled?: boolean;
    readOnly?: boolean;
    clearable?: boolean;
    autoFocus?: boolean;
    creatable?: boolean;
    defaultOpen?: boolean;
    portalled?: boolean;
    onCreateNew?: () => void;
    pending?: boolean;
}

/**
 *
 * For new code, use:
 * - `ComboboxSelect` for strict selection from predefined options with search/filter
 * - `ComboboxInput` for free-text input with optional suggestions
 *
 * This wrapper delegates to the appropriate component based on the `creatable` prop.
 */
// forwardRef so react-hook-form's <Controller> can attach field.ref without the
// "Function components cannot be given refs" warning. The ref is intentionally
// unused (Combobox delegates to Select/Input); the cast preserves the generic.
export const Combobox = forwardRef(function Combobox<T extends Item>(props: ComboboxProps<T>, _ref: Ref<unknown>) {
    const { creatable = false, ...restProps } = props;

    if (creatable) {
        return <ComboboxInput {...restProps} />;
    }

    return <ComboboxSelect {...restProps} />;
}) as <T extends Item>(props: ComboboxProps<T> & { ref?: Ref<unknown> }) => ReactElement;
